import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Label } from "./ui/label";
import { Form, FormControl, FormField, FormItem, FormMessage } from "./ui/form";
import { Input } from "./ui/input";
// import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { zodResolver } from "@hookform/resolvers/zod";
import { IoSearchOutline } from "react-icons/io5";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { RxCrossCircled } from "react-icons/rx";
import { z } from "zod";
import { useToast } from "./ui/use-toast";
import { ClipLoader } from "react-spinners";
import {
	useCreateClient,
	useCheckClient,
} from "../migration-api/api-hooks/clients";

function NewClient() {
	const { toast } = useToast();
	const [clientData, setClientData] = useState();
	const previousEmailRef = useRef("");

	// Form schema
	const formSchema = z.object({
		email: z.string().email({
			message: "Only valid email is allowed",
		}),
		phone: z.string().optional(),
		location: z.string().optional(),
		notes: z.string().optional(),
		mandateCountry: z.string().optional(),
	});

	const form = useForm({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			phone: "",
			location: "",
			mandateCountry: "",
			notes: "",
		},
	});

	const formData = form.watch();

	// Check client hook
	const { checkClient, isLoading: isChecking } = useCheckClient({
		onSuccess: (data) => {
			const returnedData = data?.data?.returnedObject;
			setClientData(returnedData);
			toast({
				variant: "success",
				title: "Success",
				description: "Client verified successfully",
			});
		},
		onError: (error) => {
			console.log("Check Client Error:", error);
			if (
				error.response.data.message ===
				'Error checking client: 400 Bad Request: "{"status":0,"message":["Unknown Account"],"badData":[],"returnedObject":null}"'
			) {
				toast({
					variant: "destructive",
					title: "Error",
					description: "Client not registered",
				});
				setClientData(undefined);
				return;
			}

			toast({
				variant: "destructive",
				title: "Error",
				description:
					error?.response?.data?.message || "Client not registered",
			});
			setClientData(undefined);
		},
	});

	// Create client hook
	const { createClient, isLoading: isCreating } = useCreateClient({
		onSuccess: (data) => {
			toast({
				variant: "success",
				title: "Success",
				description: "Client added successfully",
			});
			// Reset form and client data
			form.reset();
			setClientData(undefined);
			previousEmailRef.current = "";
		},
		onError: (error) => {
			toast({
				variant: "destructive",
				title: "Error",
				description:
					error?.response?.data?.message || "Failed to add client",
			});
		},
	});

	// Handle search for client
	const handleGs1Search = async () => {
		if (formData.email === previousEmailRef.current) return;

		// Trigger validation for the email field
		const isValid = await form.trigger("email");
		if (!isValid) return;

		setClientData(undefined);
		previousEmailRef.current = formData.email;
		checkClient({ email: formData.email });
	};

	// Validation checks
	const disableSearch = formData.email.trim() === "";
	const hasCompanyName =
		clientData?.companyName && clientData?.companyName?.trim() !== "";
	const hasCompanyEmail =
		clientData?.userEmail && clientData?.userEmail?.trim() !== "";
	const hasClientKey =
		clientData?.userKey && clientData?.userKey?.trim() !== "";

	const allChecksPass = hasCompanyName && hasCompanyEmail && hasClientKey;
	const showChecklist = clientData && !disableSearch && !isChecking;

	// Form submission
	function onSubmit(data) {
		const payload = {
			name: clientData?.companyName ?? "",
			email: clientData?.userEmail ?? "",
			phone: data.phone || undefined,
			notes: data.notes || undefined,
			mandateCountry: data.mandateCountry || undefined,
		};

		createClient(payload);
		console.log({ payload, data });
	}

	return (
		<div>
			<small className="block my-3 text-primary">
				Note: Only clients with a complete checklist can be created
			</small>
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="space-y-8"
				>
					{/* Email Field */}
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<Label>Client Email</Label>
								<FormControl>
									<div className="relative flex items-center">
										<Input
											type="email"
											placeholder="johndoe@mail.com"
											name="email"
											autoComplete="off"
											{...field}
											containerStyle="w-full"
											className="pr-11"
										/>
										<button
											type="button"
											className="absolute right-2 bg-primary text-white rounded-md p-1 disabled:bg-primary/50"
											onClick={handleGs1Search}
											disabled={disableSearch}
										>
											{isChecking ? (
												<ClipLoader
													color="white"
													size={14}
												/>
											) : (
												<IoSearchOutline />
											)}
										</button>
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					{/* Checklist Section */}
					{showChecklist && (
						<section className="space-y-4">
							<p className="text-base font-semibold">
								Client Checklist
							</p>

							<div
								className={`flex items-center gap-3 p-2 rounded-lg shadow-lg outline outline-1 outline-gray-50 ${
									hasCompanyName
										? "text-success"
										: "text-primary"
								}`}
							>
								{hasCompanyName ? (
									<IoIosCheckmarkCircleOutline size={22} />
								) : (
									<RxCrossCircled size={22} />
								)}
								<p>Company Name</p>
							</div>

							<div
								className={`flex items-center gap-3 p-2 rounded-lg shadow-lg outline outline-1 outline-gray-50 ${
									hasClientKey
										? "text-success"
										: "text-primary"
								}`}
							>
								{hasClientKey ? (
									<IoIosCheckmarkCircleOutline size={22} />
								) : (
									<RxCrossCircled size={22} />
								)}
								<p>Client Key</p>
							</div>

							<div
								className={`flex items-center gap-3 p-2 rounded-lg shadow-lg outline outline-1 outline-gray-50 ${
									hasCompanyEmail
										? "text-success"
										: "text-primary"
								}`}
							>
								{hasCompanyEmail ? (
									<IoIosCheckmarkCircleOutline size={22} />
								) : (
									<RxCrossCircled size={22} />
								)}
								<p>Registered email address</p>
							</div>
						</section>
					)}

					{/* Additional Fields - Show only when all checks pass */}
					{allChecksPass && (
						<section className="space-y-4">
							<p className="text-base font-semibold">
								Additional Information
							</p>

							{/* Phone Field */}
							<FormField
								control={form.control}
								name="phone"
								render={({ field }) => (
									<FormItem>
										<Label>Phone Number </Label>
										<FormControl>
											<Input
												type="tel"
												placeholder="+234 800 000 0000"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Location (GLN) */}
							<FormField
								control={form.control}
								name="location"
								render={({ field }) => (
									<FormItem>
										<Label>Location (GLN) </Label>
										<FormControl>
											<Input
												type="text"
												placeholder="6156000010001"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Mandate Country */}
							<FormField
								control={form.control}
								name="mandateCountry"
								render={({ field }) => (
									<FormItem>
										<Label>Mandate Country </Label>
										<FormControl>
											<Input
												type="text"
												placeholder="e.g., NGN"
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Notes Field */}
							<FormField
								control={form.control}
								name="notes"
								render={({ field }) => (
									<FormItem>
										<Label>Notes (Optional)</Label>
										<FormControl>
											<Input
												placeholder="Add any additional notes about this client..."
												className="resize-none"
												rows={4}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</section>
					)}

					{/* Submit Button */}
					{allChecksPass && (
						<Button
							type="submit"
							disabled={isChecking || isCreating}
							className="w-full"
						>
							{isCreating ? (
								<span className="flex items-center gap-2">
									<ClipLoader color="white" size={20} />
									Creating Client...
								</span>
							) : (
								<span>Create Client</span>
							)}
						</Button>
					)}
				</form>
			</Form>
		</div>
	);
}

export default NewClient;
